import { getEnumKeys } from '@gettactic/helpers';
import { capitalize } from '@gettactic/helpers/src/strings/capitalize';
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp } from 'lucide-react';
import React from 'react';
import {
  SpotLabelOrientation,
  SpotLabelPlacement,
  SpotOrientation,
  SpotShape,
  SpotShapeShape,
  SpotShapeType,
  SpotShapeTypeToLabel
} from '../../../../../types';
import { ToggleGroup, ToggleGroupItem } from '../../../../ui/toggle-group';
import { useMapEditor } from '../../../contexts/MapEditorContext';
import { DEFAULT_NODE_LABEL_ALIGNMENT } from '../../../defines';
import { DragNumberInput } from '../../controls/ScrollDragNumberInput';
import { PanelSectionWrapper } from '../ShapePanel/PanelSectionWrapper';
import { ShapeSectionOptions } from '../ShapePanel/ShapePanel';
import { Switch } from '@gettactic/components/src/@v2/components/ui/Switch';
import { useCustomParkingVerbiageOnMapBuilder as useCustomParkingVerbiage } from '../../../../../hooks/useCustomParkingVerbiageOnMapBuilder';

export interface SharedSpotPanelControlsProps extends ShapeSectionOptions {
  isSpotGroup?: boolean;
}

export const SharedSpotPanelControls: React.FC<
  SharedSpotPanelControlsProps
> = ({ shapes, isSpotGroup }) => {
  const shape = shapes[0] as SpotShape;
  const { updateShape, highlightedShape } = useMapEditor();

  const selectedItem = React.useMemo(() => {
    return shape;
  }, [shape, highlightedShape?.targetId, isSpotGroup]);

  if (!isSpotGroup && !highlightedShape) return;
  if (!selectedItem) return;

  const handleOrientationChange = (orientation: SpotOrientation) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, { orientation });
    });
  };

  const handleLabelPlacementChange = (labelPlacement: SpotLabelPlacement) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, { labelPlacement });
    });
  };

  const handleLabelOrientationChange = (
    labelOrientation: SpotLabelOrientation
  ) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, { labelOrientation });
    });
  };

  const handleSpacingChangeValue = (spacing: number | string) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, {
        spacing: Number.parseInt(String(spacing))
      });
    });
  };

  const handleRowSpacingChangeValue = (rowSpacing: number | string) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, {
        rowSpacing: Number.parseInt(String(rowSpacing))
      });
    });
  };

  const handleLabelTextAlignChange = (
    textAlign: 'center' | 'left' | 'right'
  ) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, { textAlign });
    });
  };

  const handleActive = (isActive: boolean) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, { isInactive: !isActive });
    });
  };

  const handleLabelRotationChangeValue = (rotation: number) => {
    return updateShape(shape.id, {
      textProps: {
        ...shape.textProps,
        rotation: Number.parseInt(String(rotation))
      }
    });
  };

  const handleLabelWidthChangeValue = (width: number) => {
    return updateShape(shape.id, {
      textProps: {
        ...shape.textProps,
        width: Number.parseInt(String(width))
      }
    });
  };

  const handleLabelMarginTopChangeValue = (marginTop: number) => {
    return updateShape(shape.id, {
      textProps: {
        ...shape.textProps,
        marginTop: Number.parseInt(String(marginTop))
      }
    });
  };

  const handleLabelMarginLeftChangeValue = (marginLeft: number) => {
    return updateShape(shape.id, {
      textProps: {
        ...shape.textProps,
        marginLeft: Number.parseInt(String(marginLeft))
      }
    });
  };

  const handleSpotShapeChangeValue = (spotShape: SpotShapeShape) => {
    return updateShape(shape.id, {
      spotShape
    });
  };

  const handleSpotTypeChange = (
    spotType: SpotShapeType | 'default',
    isAppearance = false
  ) => {
    shapes.forEach((shape) => {
      if (isAppearance) {
        updateShape(shape.id, {
          spotAppearance: spotType === 'default' ? undefined : spotType
        });
        return;
      }
      if (spotType !== 'default')
        updateShape(shape.id, { spotType, spotAppearance: undefined });
    });

    if (
      spotType === 'meeting_room' ||
      spotType === 'parking_lot' ||
      spotType === 'workspace'
    ) {
      handleLabelPlacementChange('outside');
    }

    if (spotType === 'meeting_room') {
      handleLabelOrientationChange('top');
    }

    if (spotType === 'desk') {
      handleLabelOrientationChange('auto');
      handleLabelPlacementChange('on-rect');
    }
  };

  const labelPlacementValue = shape.labelPlacement;
  let labelOrientation = shape.labelOrientation || 'auto';
  const labelTextAlign = shape.textAlign || DEFAULT_NODE_LABEL_ALIGNMENT;

  const isLabelForcedToAuto =
    shape.labelPlacement === 'hidden' ||
    (shape.orientation !== 'left' && shape.orientation !== 'right');

  if (isLabelForcedToAuto) {
    labelOrientation = 'auto';
  }

  const shapeAppearance = shape.spotAppearance || shape.spotType;
  const isLabelPlacementDisabled = shapeAppearance === 'meeting_room';
  const isLabelInsideDisabled = !(shapeAppearance === 'desk');
  const isLabelAutoDisabled = shapeAppearance === 'meeting_room';

  const customParkingVerbiage = useCustomParkingVerbiage();

  const isDeskAppearance =
    (selectedItem.spotType === 'desk' &&
      (selectedItem.spotAppearance === 'desk' ||
        selectedItem.spotAppearance === undefined)) ||
    (selectedItem.spotType !== 'desk' &&
      selectedItem.spotAppearance === 'desk');

  const spotTitle = React.useMemo(() => {
    if (shape.type !== 'spot') return '';

    const spotShape = shape as SpotShape;

    // @ts-ignore <'parking_space' is not added to SpotType yet>
    const spotIsParking =
      spotShape.spotType === 'parking_lot' ||
      // @ts-ignore <'parking_space' is not added to SpotType yet>
      spotShape.spotType === 'parking_space';

    let title = capitalize(SpotShapeTypeToLabel[spotShape.spotType]);
    if (spotIsParking && customParkingVerbiage !== null) {
      title = `${capitalize(customParkingVerbiage)} Group`;
    }
    return title;
  }, [shape.type, customParkingVerbiage, shape]);

  return (
    <div className="flex flex-col gap-3">
      {selectedItem.resource_id ? (
        <PanelSectionWrapper title={'Resource'}>
          <div>{selectedItem.resource_id}</div>
        </PanelSectionWrapper>
      ) : null}
      <PanelSectionWrapper title={'Resource'}>
        <div className="flex flex-col gap-1">
          <div className="text-xs font-medium">Type</div>
          <ToggleGroup
            size={'xs'}
            type="single"
            value={selectedItem.spotType}
            onValueChange={(value) => {
              if (value) handleSpotTypeChange(value as SpotShapeType);
            }}
          >
            {getEnumKeys(SpotShapeType)
              .filter((type) => type !== 'Node')
              .map((type) => (
                <ToggleGroupItem
                  size={'xs'}
                  value={SpotShapeType[type]}
                  key={type}
                >
                  <span>
                    {type === 'Parking'
                      ? capitalize(customParkingVerbiage ?? 'Parking')
                      : type}
                  </span>
                </ToggleGroupItem>
              ))}
          </ToggleGroup>
        </div>

        <div className="flex flex-col gap-1">
          <div className="font-medium text-xs">Appearance</div>
          <ToggleGroup
            size={'xs'}
            type="single"
            value={selectedItem.spotAppearance || 'default'}
            onValueChange={(value) => {
              if (value) handleSpotTypeChange(value as SpotShapeType, true);
            }}
          >
            <ToggleGroupItem size={'xs'} value={'default'} key={'default'}>
              <span>Default</span>
            </ToggleGroupItem>
            {getEnumKeys(SpotShapeType).map((type) => (
              <ToggleGroupItem
                size={'xs'}
                value={SpotShapeType[type]}
                key={type}
              >
                <span>
                  {type === 'Parking'
                    ? capitalize(customParkingVerbiage ?? 'Parking')
                    : type}
                </span>
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
        {isDeskAppearance ? (
          <div className="flex flex-col gap-1">
            <div className="font-medium text-xs">Desk Shape</div>
            <select
              className={'w-full rounded-sm border-input'}
              onChange={(e) => {
                handleSpotShapeChangeValue(e.target.value as SpotShapeShape);
              }}
            >
              <option value={'default'}>Default</option>
            </select>
          </div>
        ) : undefined}
        <div className="flex flex-col gap-1">
          <div className="font-medium text-xs">{spotTitle} Orientation</div>
          <ToggleGroup
            size={'xs'}
            type="single"
            value={
              isLabelPlacementDisabled ? undefined : selectedItem.orientation
            }
            onValueChange={(value) => {
              if (value) handleOrientationChange(value as SpotOrientation);
            }}
            disabled={isLabelPlacementDisabled}
          >
            <ToggleGroupItem size={'xs'} value="top" aria-label="Top">
              <ArrowUp className="h-3 w-3" />
              <span>Top</span>
            </ToggleGroupItem>
            <ToggleGroupItem value="bottom" size={'xs'} aria-label="Bottom">
              <ArrowDown className="h-3 w-3" />
              <span>Bottom</span>
            </ToggleGroupItem>
            <ToggleGroupItem
              value="left"
              aria-label="Left"
              size={'xs'}
              className="flex items-center gap-1 text-xs"
            >
              <ArrowLeft className="h-3 w-3" />
              <span>Left</span>
            </ToggleGroupItem>
            <ToggleGroupItem value="right" size={'xs'} aria-label="Right">
              <ArrowRight className="h-3 w-3" />
              <span>Right</span>
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
        <div className="flex flex-col gap-1">
          <div className="text-xs font-medium">Label Type</div>
          <ToggleGroup
            size={'xs'}
            type="single"
            value={labelPlacementValue}
            onValueChange={(value) => {
              if (value)
                handleLabelPlacementChange(value as SpotLabelPlacement);
            }}
          >
            <ToggleGroupItem
              disabled={isLabelInsideDisabled}
              value="on-rect"
              aria-label="Top"
              size={'xs'}
            >
              <span>Inside</span>
            </ToggleGroupItem>
            {/*<ToggleGroupItem value="outside" aria-label="Outside" size={'xs'}>
              <span>Outside</span>
            </ToggleGroupItem>*/}
            <ToggleGroupItem
              value="outside-node"
              aria-label="Outside Resource"
              size={'xs'}
            >
              <span>Outside</span>
            </ToggleGroupItem>
            <ToggleGroupItem value="hidden" aria-label="Hidden" size={'xs'}>
              <span>Hidden</span>
            </ToggleGroupItem>
            {/* This is disabled for now, but the functionality is still there */}
            {/*<ToggleGroupItem value="custom" aria-label="Top" size={'xs'}>
              <span>Custom</span>
            </ToggleGroupItem>*/}
          </ToggleGroup>
        </div>
        <div className="flex flex-col gap-1">
          <div className="text-xs font-medium">Label Position</div>
          <ToggleGroup
            size={'xs'}
            type="single"
            value={labelOrientation}
            onValueChange={(value) => {
              if (value)
                handleLabelOrientationChange(value as SpotLabelOrientation);
            }}
            disabled={isLabelForcedToAuto}
          >
            <ToggleGroupItem
              disabled={isLabelAutoDisabled}
              value="auto"
              aria-label="Auto"
              size={'xs'}
            >
              <span>Auto</span>
            </ToggleGroupItem>
            <ToggleGroupItem value="top" aria-label="Top" size={'xs'}>
              <span>Top</span>
            </ToggleGroupItem>
            <ToggleGroupItem value="bottom" aria-label="Bottom" size={'xs'}>
              <span>Bottom</span>
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
        <div>
          {/* biome-ignore lint/a11y/noLabelWithoutControl: <explanation> */}
          <label className={'flex items-center gap-2'}>
            <Switch
              checked={!shape.isInactive}
              onCheckedChange={handleActive}
            />
            Active
          </label>
        </div>
      </PanelSectionWrapper>
      {labelPlacementValue === 'custom' && (
        <PanelSectionWrapper title={'Label position'}>
          <div className={'flex gap-2 w-full'}>
            <div className="flex flex-col gap-1 w-full">
              <div className="text-sm font-medium">Rotation</div>
              <DragNumberInput
                size={'sm'}
                min={-360}
                max={360}
                value={selectedItem.textProps?.rotation || 0}
                onChange={handleLabelRotationChangeValue}
              />
            </div>
            <div className="flex flex-col gap-1 w-full">
              <div className="text-sm font-medium">Width</div>
              <DragNumberInput
                size={'sm'}
                min={10}
                max={100}
                value={selectedItem.textProps?.width || 0}
                onChange={handleLabelWidthChangeValue}
              />
            </div>
          </div>
          <div className={'flex gap-2'}>
            <div className="flex flex-col gap-1">
              <div className="text-sm font-medium">Top</div>
              <DragNumberInput
                size={'sm'}
                value={selectedItem.textProps?.marginTop || 0}
                onChange={handleLabelMarginTopChangeValue}
              />
            </div>
            <div className="flex flex-col gap-1">
              <div className="text-sm font-medium">Left</div>
              <DragNumberInput
                size={'sm'}
                value={selectedItem.textProps?.marginLeft || 0}
                onChange={handleLabelMarginLeftChangeValue}
              />
            </div>
          </div>
        </PanelSectionWrapper>
      )}
    </div>
  );
};
